// Offline support for field entry with no signal: the pumper well check,
// the tour-sheet rig report and the hydrotest ticket.
//
// - cache: last good copy of the pumper's routes (wells) and each well's
//   tanks, so the route and the tank gauge form still open offline.
// - check queue: a well check submitted without signal is stored here and
//   uploaded automatically when the connection returns. Every check has an
//   idempotency key (sent as the Idempotency-Key header) created once per
//   check, so a retry after a lost response can never create a duplicate —
//   the server answers a repeat with the original check.
// - the same queue carries rig report saves (kind "rigReport", see
//   services/rigReportOffline.js): a create rides on its Idempotency-Key,
//   and later saves of an uploaded report are PUTs keyed per report, so a
//   newer save made offline replaces the older queued one.
// - hydro tickets (kind "hydroTicket", services/hydroOffline.js) work the
//   same way; a queued create stores its server id under item.serverIdKey.
//
// (The old generic sync engine and its "rigiq" IndexedDB were retired —
// docs/audit/FEATURE-OVERLAP.md #1.)
import { openDB } from "idb";
import axios from "axios";

const DB_NAME = "rigiq_field";
const DB_VERSION = 1;
export const QUEUE_EVENT = "rigiq:field-queue";

let dbPromise = null;
function db() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(d) {
        if (!d.objectStoreNames.contains("cache")) d.createObjectStore("cache", { keyPath: "key" });
        if (!d.objectStoreNames.contains("checkQueue")) d.createObjectStore("checkQueue", { keyPath: "key" });
      },
    });
  }
  return dbPromise;
}

// Test hook: forget the open connection (fake-indexeddb resets).
export function _resetForTests() {
  dbPromise = null;
}

function notify() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(QUEUE_EVENT));
}

// ─── cache ─────────────────────────────────────────────────────────────
export async function cacheSet(key, data) {
  try {
    await (await db()).put("cache", { key, data, savedAt: new Date().toISOString() });
  } catch {
    // storage full / private mode — offline copy is best-effort
  }
}

export async function cacheGet(key) {
  try {
    const row = await (await db()).get("cache", key);
    return row ? row.data : undefined;
  } catch {
    return undefined;
  }
}

export async function cacheDelete(key) {
  try {
    await (await db()).delete("cache", key);
  } catch {
    // best-effort
  }
}

/** Every cache entry whose key starts with one of the prefixes. */
export async function cacheEntries(prefixes) {
  try {
    const rows = await (await db()).getAll("cache");
    return rows.filter((r) => prefixes.some((p) => String(r.key).startsWith(p)));
  } catch {
    return [];
  }
}

/** The organization a session token belongs to (JWT payload), or null. */
export function tokenOrg(token) {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=")));
    const org = json.organizationId;
    return org ? String(typeof org === "object" ? org._id || org.id || "" : org) : null;
  } catch {
    return null;
  }
}

// GET with offline fallback: online -> fresh data (and refresh the cache);
// no signal -> the cached copy, if there is one.
export async function getWithOfflineCache(cacheKey, request) {
  try {
    const data = await request();
    await cacheSet(cacheKey, data);
    return { data, fromCache: false };
  } catch (err) {
    if (!isOfflineError(err)) throw err;
    const cached = await cacheGet(cacheKey);
    if (cached === undefined) throw err;
    return { data: cached, fromCache: true };
  }
}

// ─── errors ────────────────────────────────────────────────────────────
// No response at all (no signal / DNS / timeout), the service worker's
// own offline 503 (public/sw.js), or a gateway error while the server is
// unreachable — all mean "try again later", never "the data was bad".
export function isOfflineError(err) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (!err || !err.response) return true;
  const s = err.response.status;
  return s === 502 || s === 503 || s === 504;
}

export function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `wc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// ─── check queue ───────────────────────────────────────────────────────
export async function enqueueCheck({ key, payload, wellName }) {
  await (await db()).put("checkQueue", {
    key,
    payload,
    wellName: wellName || "",
    createdAt: new Date().toISOString(),
    attempts: 0,
    status: "pending",
    lastError: "",
  });
  notify();
}

// Any other queued request (rig reports): { key, kind, method, url (path
// under the API base), payload, label }. Putting an item with a key that's
// already queued replaces it — the newest save wins, uploaded once.
export async function enqueueRequest({ key, kind, method = "post", url, payload, label, serverIdKey, orgId }) {
  await (await db()).put("checkQueue", {
    key,
    kind,
    method,
    url,
    payload,
    serverIdKey: serverIdKey || "",
    // Which organization this belongs to — never uploaded into another one
    // (a demo re-seed creates a new organization).
    orgId: orgId || "",
    wellName: label || "",
    createdAt: new Date().toISOString(),
    attempts: 0,
    status: "pending",
    lastError: "",
  });
  notify();
}

export async function getQueued(key) {
  try {
    return await (await db()).get("checkQueue", key);
  } catch {
    return undefined;
  }
}

export async function listQueuedChecks() {
  try {
    const rows = await (await db()).getAll("checkQueue");
    return rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  } catch {
    return [];
  }
}

// { [wellId]: "pending" | "failed" } for the route list. A well with any
// check still waiting shows "pending" even if an older one was rejected.
export function queuedStatusByWell(rows) {
  const out = {};
  (rows || []).forEach((r) => {
    if (r.kind && r.kind !== "wellCheck") return;
    const wellId = r.payload && r.payload.wellId;
    if (!wellId) return;
    if (r.status === "pending" || !out[wellId]) out[wellId] = r.status === "pending" ? "pending" : "failed";
  });
  return out;
}

export async function discardQueuedCheck(key) {
  await (await db()).delete("checkQueue", key);
  notify();
}

let flushing = null;

// Uploads every pending check, oldest first. Stops at the first one that
// fails for lack of signal (the rest would too). A check the server
// REJECTS (4xx other than auth) is marked failed and kept so the pumper
// sees it; 401 (expired session) leaves it pending for the next login.
// Concurrent calls share one run.
export function flushCheckQueue(token, apiBase = process.env.REACT_APP_API_URL) {
  if (!token) return Promise.resolve({ uploaded: 0 });
  if (flushing) return flushing;
  flushing = (async () => {
    let uploaded = 0;
    try {
      const pending = (await listQueuedChecks()).filter((c) => c.status === "pending");
      const org = tokenOrg(token);
      for (const item of pending) {
        // Saved for a different organization (e.g. before a demo re-seed):
        // set aside on the phone, never uploaded here.
        // Hydro tickets saved before items carried their organization are
        // set aside too — they may belong to data that no longer exists.
        if ((item.orgId && org && item.orgId !== org) || (item.kind === "hydroTicket" && item.orgId === undefined)) {
          // eslint-disable-next-line no-await-in-loop
          await cacheSet(`quarantine:${item.key}`, { ...item, quarantinedAt: new Date().toISOString(), reason: "other-organization" });
          // eslint-disable-next-line no-await-in-loop
          await (await db()).delete("checkQueue", item.key);
          continue; // eslint-disable-line no-continue
        }
        try {
          const isCheck = !item.kind || item.kind === "wellCheck";
          const send = isCheck || item.method !== "put" ? axios.post : axios.put;
          // eslint-disable-next-line no-await-in-loop
          const res = await send(`${apiBase}${isCheck ? "/api/well-checks" : item.url}`, item.payload, {
            headers: { Authorization: "Bearer " + token, "Idempotency-Key": item.key },
          });
          // A rig report created offline: remember its server id so the
          // form switches to updating it.
          if (item.kind === "rigReport" && res && res.data && res.data._id) {
            // eslint-disable-next-line no-await-in-loop
            await cacheSet(`rrServerId:${item.key}`, res.data._id);
          }
          if (item.serverIdKey && res && res.data && res.data._id) {
            // eslint-disable-next-line no-await-in-loop
            await cacheSet(item.serverIdKey, res.data._id);
          }
          // eslint-disable-next-line no-await-in-loop
          await (await db()).delete("checkQueue", item.key);
          uploaded += 1;
        } catch (err) {
          if (isOfflineError(err) || err.response.status === 401 || err.response.status === 429 || err.response.status >= 500) {
            // eslint-disable-next-line no-await-in-loop
            await (await db()).put("checkQueue", { ...item, attempts: item.attempts + 1, lastError: "" });
            break;
          }
          // Saved but not submitted (hydro ticket with errors): the record
          // exists now — remember its id so the form updates it instead of
          // creating another.
          const saved = err.response.data && err.response.data.ticket;
          if (item.serverIdKey && saved && saved._id) {
            // eslint-disable-next-line no-await-in-loop
            await cacheSet(item.serverIdKey, saved._id);
          }
          // eslint-disable-next-line no-await-in-loop
          await (await db()).put("checkQueue", {
            ...item,
            attempts: item.attempts + 1,
            status: "failed",
            lastError: err.response.data?.error || `HTTP ${err.response.status}`,
          });
        }
      }
    } finally {
      flushing = null;
      notify();
    }
    return { uploaded };
  })();
  return flushing;
}
